import type { ChangeEvent, ReactNode } from "react";

import { Spin } from "antd";

import { AntThemed } from "@/components/ui/ant-theme";
import { Button } from "@/components/ui/button";
import { columnKey } from "@/utils/csv";
import { cn } from "@/utils/utils";
import { useTranslation } from "@/store/locale";

/** How many rows a page of the review grid shows. */
export const PAGE_ROWS = 100;

/** Which rows a page number lands on, for the grid and for what Submit sends. */
export function pageSlice(rows: string[][], page: number) {
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_ROWS));
  const current = Math.min(page, pages);
  const start = (current - 1) * PAGE_ROWS;
  return { pages, current, start, shown: rows.slice(start, start + PAGE_ROWS) };
}

/**
 * The file, opened for correction before it is sent.
 *
 * A batch file is usually right and occasionally wrong in one cell, and the
 * round trip for that cell was: send it, read which rows the server rejected,
 * open the file again in a spreadsheet, find them, fix, re-send. This is the
 * same file with the fixing in the middle.
 *
 * A hundred rows to a page because the grid is inputs rather than text — a few
 * thousand of those at once is a page that stops responding to typing, which
 * is the one thing an editor may not do.
 */
/**
 * What a column's cells may hold, so the editor offers the right control: a
 * fixed choice becomes a select, a date a date field, and free text stays an
 * input with the keyboard hinted. The choices are the spellings the server
 * parses — offering anything else would be offering a rejection.
 */
/** Why a row was refused, and which of its columns the answer is about. */
export interface RowError {
  message: string;
  /**
   * The `columnKey`-shaped columns the problem names, empty for one that
   * names none. More than one where the rule reads more than one: a duplicate
   * identity is the first name, the last name and the birth date together, and
   * marking one of the three would say the other two are fine.
   */
  fields: string[];
}

/**
 * What a column is, as far as the grid is concerned: text, with a keyboard
 * hint on the ones that are all digits. Every cell is the same control —
 * see `EditableCell` for why.
 */
export type CellKind = { kind: "text"; inputMode?: "numeric" | "tel" | "email" };

export function CsvEditor({
  errors,
  header,
  rows,
  page,
  onPageChange,
  onChange,
  busy,
  types,
  lead,
  readOnly = false,
}: {
  header: string[];
  rows: string[][];
  /** Which page is shown. Held by the page around this, which submits by it. */
  page: number;
  onPageChange: (page: number) => void;
  onChange: (rows: string[][]) => void;
  busy: boolean;
  /**
   * What was said about each row last time it was sent, aligned to `rows`. A
   * list per row, because a row can be wrong in more than one way at once and
   * each complaint belongs against its own cell.
   */
  errors?: (RowError[] | undefined)[];
  /** Cell kinds by column name; a column not named is free text. */
  types?: Record<string, CellKind>;
  /**
   * Whatever belongs above the table: the file being shown, and the way to
   * change it. Here rather than in the dialog around it because the answer to
   * "this is the wrong file" has to be within reach of the rows that say so.
   */
  lead?: ReactNode;
  /** The same table with the cells as text — the Success tab, where the rows
   *  are a record and not work. One component, so the two tabs cannot drift. */
  readOnly?: boolean;
}) {
  const t = useTranslation();
  const { pages, current, start, shown } = pageSlice(rows, page);

  // One row copied, not ten thousand: a keystroke that rebuilds every row of a
  // full file is a keystroke you can watch arrive.
  const edit = (rowIndex: number, cellIndex: number, value: string) => {
    const index = start + rowIndex;
    const row = rows[index];
    if (!row) return;
    const edited = row.slice();
    edited[cellIndex] = value;
    const next = rows.slice();
    next[index] = edited;
    onChange(next);
  };

  return (
    <div className="grid min-w-0 gap-3">
      {lead}

      {/* The rows scroll, not the dialog: the pager under this box and the file
          above it stay where they are, so "which page am I on" and "wrong
          file" are answerable from row four hundred as well as row one. The
          head is sticky inside it, which is what makes a scrolled body
          readable at all.

          While a run is in the air the whole table is behind a spinner: the
          cells are still on screen, which is what somebody watching wants to
          see, and none of them can be typed into while the rows they hold are
          being sent. */}
      <AntThemed>
        {/* `min-w-0` all the way down: a grid item is `min-width: auto` by
            default, so a table twenty columns wide would set the width of the
            box that is supposed to be clipping it — and the dialog, not the
            body, would be what scrolls sideways. */}
        <Spin
          spinning={busy}
          wrapperClassName="block w-full min-w-0 [&>.ant-spin-container]:min-w-0"
        >
          <div className="border-line max-h-[min(52dvh,28rem)] min-w-0 overflow-auto rounded-xl border">
            <table className="w-max min-w-full border-collapse text-left">
              <thead className="bg-surface-inset sticky top-0 z-10">
                <tr>
                  {header.map((column) => (
                    <th
                      key={column}
                      className="text-fg-muted text-footnote px-3 py-2 font-medium whitespace-nowrap"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shown.map((row, rowIndex) => {
                  const refused = errors?.[start + rowIndex];
                  return (
                    <tr
                      key={start + rowIndex}
                      // No tint across the row: the cells the server named
                      // carry their own red box and their own sentence, and a
                      // red band behind twenty columns says "something here"
                      // over nineteen that are fine. A complaint that names no
                      // column is in the block above the table, which is where
                      // a whole-row problem belongs.
                      className="[&:not(:first-child)_td]:border-line"
                    >
                      {header.map((column, cellIndex) => (
                        <td
                          key={column}
                          className={
                            readOnly ? "text-fg text-footnote px-3 py-1.5" : "px-1.5 py-1"
                          }
                        >
                          {readOnly ? (
                            (row[cellIndex] ?? "")
                          ) : (
                            <EditableCell
                              column={column}
                              value={row[cellIndex] ?? ""}
                              kind={types?.[column]}
                              busy={busy}
                              line={start + rowIndex + 2}
                              // Every complaint that names this column, under
                              // the cell it is about: a red box says "something
                              // here", the sentences say what, at the one place
                              // where the fix is typed.
                              messages={(refused ?? [])
                                .filter((problem) => problem.fields.includes(columnKey(column)))
                                .map((problem) => problem.message)}
                              onEdit={(value) => edit(rowIndex, cellIndex, value)}
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Spin>
      </AntThemed>

      {/* Under the table, where a table's pager belongs — and the count beside
          it, so "which rows am I looking at" and "how do I see the rest" are
          one line rather than two ends of the dialog. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-fg-muted text-footnote tabular-nums">
          {t("mem.csvRowsOnPage")
            .replace("{from}", String(rows.length === 0 ? 0 : start + 1))
            .replace("{to}", String(start + shown.length))
            .replace("{total}", String(rows.length))}
        </p>
        {pages > 1 ? (
          <span className="flex items-center gap-1.5">
            <Button
              variant="secondary"
              size="sm"
              disabled={current === 1 || busy}
              onClick={() => onPageChange(current - 1)}
            >
              {t("common.previousPage")}
            </Button>
            <span className="text-fg-muted text-footnote tabular-nums">
              {t("rp.pageN").replace("{n}", `${current} / ${pages}`)}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={current === pages || busy}
              onClick={() => onPageChange(current + 1)}
            >
              {t("common.nextPage")}
            </Button>
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * One cell: a plain text box, and nothing else.
 *
 * This is the legacy grid's cell, deliberately. Every column there is one
 * input of the same kind — no select, no date picker — because a spreadsheet
 * exported from somewhere else holds whatever it holds, and a control that
 * only accepts its own vocabulary either blanks a cell it cannot represent or
 * argues with a value the server would have taken. A text box shows the file
 * as it is and sends back what was typed; the server is the one that judges.
 *
 * It is drawn as text until it is wanted: transparent border and background,
 * so a page of them reads as a table rather than a wall of boxes; a border on
 * hover to say it can be edited, and the accent plus a solid ground on focus
 * to say it is being.
 */
function EditableCell({
  column,
  value,
  kind,
  busy,
  line,
  messages,
  onEdit,
}: {
  column: string;
  value: string;
  /** Still read for the keyboard hint on a numeric column; nothing else. */
  kind: CellKind | undefined;
  busy: boolean;
  line: number;
  /**
   * What the server said about this cell, in its own words, printed under the
   * box — and what makes the box red. A list: one cell can be refused for more
   * than one reason.
   */
  messages?: string[];
  onEdit: (value: string) => void;
}) {
  const t = useTranslation();
  const invalid = Boolean(messages?.length);
  const box = (
    <input
      type="text"
      value={value}
      inputMode={kind?.kind === "text" ? kind.inputMode : undefined}
      disabled={busy}
      aria-invalid={invalid || undefined}
      aria-label={`${column} ${t("mem.csvLine")} ${line}`}
      onChange={(event: ChangeEvent<HTMLInputElement>) => onEdit(event.target.value)}
      className={cn(
        "text-callout text-fg w-full min-w-32 rounded-md border border-transparent bg-transparent px-1.5 py-1",
        "transition-[border-color,background-color] duration-[var(--duration-fast)] outline-none",
        "hover:border-line-strong",
        "focus:border-accent focus:bg-surface-raised",
        "disabled:cursor-not-allowed disabled:opacity-60",
        // A cell the server refused keeps its box whether or not anyone is
        // pointing at it: that is the one thing on the grid worth finding.
        invalid && "border-danger bg-danger-soft",
      )}
    />
  );
  if (!invalid) return box;
  return (
    <span className="grid gap-1">
      {box}
      {messages?.map((message) => (
        <span key={message} className="text-danger text-footnote">
          {message}
        </span>
      ))}
    </span>
  );
}
